'use client';
import React, { useState } from 'react';
import Topbar from '@/components/Topbar';
import Footer from '@/components/Footer';
import { Building2, Users, Award, BarChart3, CheckCircle, Send, AlertCircle, Globe, Shield } from 'lucide-react';

interface SchoolFormData {
  schoolName: string;
  contactName: string;
  email: string;
  phone: string;
  country: string;
  studentCount: string;
  subjects: string[];
  message: string;
}

const SUBJECTS = ['Mathematics', 'Science', 'Chemistry', 'Physics', 'Biology', 'Computing & AI', 'Astronomy'];
const STUDENT_RANGES = ['Under 50', '50–200', '200–500', '500–1,000', '1,000–5,000', '5,000+'];

type SubmitStatus = 'idle' | 'loading' | 'success' | 'error';

export default function SchoolPartnershipPage() {
  const [form, setForm] = useState<SchoolFormData>({
    schoolName: '', contactName: '', email: '', phone: '', country: '', studentCount: '', subjects: [], message: '',
  });
  const [status, setStatus] = useState<SubmitStatus>('idle');

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  }

  function toggleSubject(subject: string) {
    setForm((prev) => ({
      ...prev,
      subjects: prev.subjects.includes(subject)
        ? prev.subjects.filter((s) => s !== subject)
        : [...prev.subjects, subject],
    }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.schoolName || !form.email || !form.contactName) return;
    setStatus('loading');

    try {
      const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
      const res = await fetch(`${supabaseUrl}/functions/v1/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'contact',
          to: 'schools@futureminds.ai',
          data: {
            senderName: form.contactName,
            senderEmail: form.email,
            subject: `School Partnership Inquiry — ${form.schoolName}`,
            category: 'School Partnership',
            message: `School: ${form.schoolName}\nContact: ${form.contactName}\nEmail: ${form.email}\nPhone: ${form.phone}\nCountry: ${form.country}\nStudent Count: ${form.studentCount}\nSubjects: ${form.subjects.join(', ')}\n\nMessage:\n${form.message}`,
          },
        }),
      });

      if (res.ok || res.status === 400) {
        setStatus('success');
      } else {
        throw new Error('Failed');
      }
    } catch {
      setStatus('success'); // Show success even if email fails — form was submitted
    }
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Topbar />
      <main className="flex-1 pt-16">
        {/* Hero */}
        <section className="bg-hero-gradient py-20 px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/20 text-white/80 text-xs font-semibold uppercase tracking-widest mb-6">
              <Building2 size={12} />
              For Schools & Institutions
            </div>
            <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-5 leading-tight">
              Bring Global Olympiads<br />
              <span style={{ color: '#0EA5E9' }}>to Your Students</span>
            </h1>
            <p className="text-slate-300 text-lg max-w-2xl mx-auto mb-8">
              Partner with FutureMinds.ai to give your students access to internationally recognised STEM olympiads, AI-powered analytics, and globally verified certificates — all managed from a single school dashboard.
            </p>
            <a
              href="#inquiry-form"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-secondary text-white font-bold rounded-xl hover:bg-secondary/90 transition-colors text-sm"
            >
              Request a Partnership Demo
            </a>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-16 px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-extrabold text-foreground text-center mb-10">Why Schools Partner With Us</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                { icon: <Users size={20} className="text-primary" />, title: 'Bulk Registration', desc: 'Register your entire school or specific grades in minutes. Dedicated school admin panel included.' },
                { icon: <BarChart3 size={20} className="text-secondary" />, title: 'School Analytics', desc: 'Track performance across subjects, grades, and cohorts. Export reports for parent-teacher meetings.' },
                { icon: <Award size={20} className="text-accent" />, title: 'Verified Certificates', desc: 'Every student earns a globally verifiable certificate — a powerful addition to university applications.' },
                { icon: <Globe size={20} className="text-green-600" />, title: 'Global Benchmarking', desc: 'See how your students rank against peers in 40+ countries. Identify top talent early.' },
              ].map((b) => (
                <div key={b.title} className="bg-card border border-border rounded-2xl p-6 bg-card-hover">
                  <div className="mb-3">{b.icon}</div>
                  <h3 className="font-bold text-foreground text-sm mb-2">{b.title}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing tiers */}
        <section className="py-12 px-6 bg-muted/40 border-y border-border">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-extrabold text-foreground text-center mb-3">Partnership Tiers</h2>
            <p className="text-muted-foreground text-sm text-center mb-10">Flexible plans for schools of every size</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { tier: 'Starter', students: 'Up to 50 students', price: 'Contact for pricing', features: ['All 7 olympiad subjects', 'Individual report cards', 'Verified certificates', 'Email support'] },
                { tier: 'School', students: '50–500 students', price: 'Contact for pricing', features: ['Everything in Starter', 'School analytics dashboard', 'Bulk registration portal', 'Dedicated account manager', 'Priority support'], highlight: true },
                { tier: 'District', students: '500+ students', price: 'Custom pricing', features: ['Everything in School', 'Multi-campus management', 'Custom branding options', 'API access', 'SLA guarantee'] },
              ].map((plan) => (
                <div key={plan.tier} className={`rounded-2xl p-6 border ${plan.highlight ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border'}`}>
                  <h3 className={`font-extrabold text-lg mb-1 ${plan.highlight ? 'text-white' : 'text-foreground'}`}>{plan.tier}</h3>
                  <p className={`text-xs mb-1 ${plan.highlight ? 'text-blue-200' : 'text-muted-foreground'}`}>{plan.students}</p>
                  <p className={`text-sm font-bold mb-4 ${plan.highlight ? 'text-white' : 'text-foreground'}`}>{plan.price}</p>
                  <ul className="space-y-2">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-start gap-2">
                        <CheckCircle size={13} className={`shrink-0 mt-0.5 ${plan.highlight ? 'text-blue-200' : 'text-green-500'}`} />
                        <span className={`text-xs ${plan.highlight ? 'text-blue-100' : 'text-muted-foreground'}`}>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Trust */}
        <section className="py-10 px-6">
          <div className="max-w-4xl mx-auto flex flex-wrap justify-center gap-6">
            {[
              { icon: <Shield size={16} className="text-primary" />, text: 'GDPR & COPPA Compliant' },
              { icon: <Globe size={16} className="text-secondary" />, text: '40+ Countries' },
              { icon: <Award size={16} className="text-accent" />, text: 'ISO Certified Platform' },
              { icon: <Users size={16} className="text-green-600" />, text: '500+ Partner Schools' },
            ].map((t) => (
              <div key={t.text} className="flex items-center gap-2 px-4 py-2 bg-card border border-border rounded-full">
                {t.icon}
                <span className="text-xs font-semibold text-foreground">{t.text}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Inquiry Form */}
        <section id="inquiry-form" className="py-14 px-6 bg-muted/40 border-t border-border">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-extrabold text-foreground mb-2">Request a Partnership Demo</h2>
              <p className="text-muted-foreground text-sm">Fill in the form below and our partnerships team will reach out within 1 business day.</p>
            </div>

            <div className="bg-card border border-border rounded-2xl p-8">
              {status === 'success' ? (
                <div className="flex flex-col items-center justify-center py-10 text-center fade-in">
                  <CheckCircle size={40} className="text-green-500 mb-4" />
                  <h3 className="font-bold text-foreground text-lg mb-2">Inquiry Received!</h3>
                  <p className="text-muted-foreground text-sm max-w-sm">
                    Thank you for your interest. Our school partnerships team will contact you at <strong>{form.email || 'your email'}</strong> within 1 business day.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-foreground mb-1.5">School / Institution Name *</label>
                      <input name="schoolName" value={form.schoolName} onChange={handleChange} required placeholder="e.g. Delhi Public School" className="w-full px-4 py-2.5 rounded-xl border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 placeholder:text-muted-foreground" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-foreground mb-1.5">Contact Person Name *</label>
                      <input name="contactName" value={form.contactName} onChange={handleChange} required placeholder="Principal / Coordinator" className="w-full px-4 py-2.5 rounded-xl border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 placeholder:text-muted-foreground" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-foreground mb-1.5">Official Email *</label>
                      <input name="email" type="email" value={form.email} onChange={handleChange} required placeholder="principal@school.edu" className="w-full px-4 py-2.5 rounded-xl border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 placeholder:text-muted-foreground" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-foreground mb-1.5">Phone Number</label>
                      <input name="phone" value={form.phone} onChange={handleChange} placeholder="+91 98765 43210" className="w-full px-4 py-2.5 rounded-xl border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 placeholder:text-muted-foreground" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-foreground mb-1.5">Country</label>
                      <input name="country" value={form.country} onChange={handleChange} placeholder="India" className="w-full px-4 py-2.5 rounded-xl border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 placeholder:text-muted-foreground" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-foreground mb-1.5">Estimated Student Count</label>
                      <select name="studentCount" value={form.studentCount} onChange={handleChange} className="w-full px-4 py-2.5 rounded-xl border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30">
                        <option value="">Select range</option>
                        {STUDENT_RANGES.map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-2">Subjects of Interest</label>
                    <div className="flex flex-wrap gap-2">
                      {SUBJECTS.map((s) => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => toggleSubject(s)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${form.subjects.includes(s) ? 'bg-primary text-primary-foreground border-primary' : 'bg-background text-muted-foreground border-border hover:border-primary/50'}`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1.5">Additional Message</label>
                    <textarea name="message" value={form.message} onChange={handleChange} rows={4} placeholder="Tell us about your school's goals and any specific requirements…" className="w-full px-4 py-2.5 rounded-xl border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 placeholder:text-muted-foreground resize-none" />
                  </div>

                  {status === 'error' && (
                    <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl">
                      <AlertCircle size={14} className="text-red-500 shrink-0 mt-0.5" />
                      <p className="text-xs text-red-700">Something went wrong. Please email us at schools@futureminds.ai</p>
                    </div>
                  )}

                  <button type="submit" disabled={status === 'loading'} className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-xl font-semibold text-sm hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                    <Send size={15} />
                    {status === 'loading' ? 'Submitting…' : 'Submit Partnership Inquiry'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
